import { useState, useEffect, useRef } from 'react';
import { useSEO } from '../../hooks/useSEO';

const KontaktPage = () => {
  const siteUrl = import.meta.env.VITE_SITE_URL || 'https://bamme-media.de';

  useSEO({
    title: 'Kontakt | Bamme Media Wernigerode – Jetzt Projekt anfragen',
    description: 'Kontaktiere Bamme Media in Wernigerode. Wir beraten dich kostenlos zu Webdesign, Social Media & Fotografie. Ruf an oder schreib uns – wir melden uns schnell!',
    keywords: 'Kontakt Bamme Media, Wernigerode Agentur, Projekt anfragen, Webdesign Beratung, Social Media Kontakt',
    canonical: `${siteUrl}/kontakt`,
    robots: 'index, follow',
    ogTitle: 'Kontakt | Bamme Media Wernigerode',
    ogDescription: 'Kontaktiere Bamme Media in Wernigerode. Kostenlose Beratung zu Webdesign, Social Media & Fotografie.',
    ogImage: 'https://static.readdy.ai/image/8fd098698619b27c03cb78789aa098a8/fa291c43a7c1ed26d5ebb74eb50592e0.png',
    geoPosition: '51.8312,10.7865',
    geoRegion: 'DE-ST',
    geoPlacename: 'Wernigerode',
    jsonLd: {
      "@context": "https://schema.org",
      "@type": "ContactPage",
      "name": "Kontakt – Bamme Media",
      "url": `${siteUrl}/kontakt`,
      "description": "Kontaktseite von Bamme Media in Wernigerode",
      "mainEntity": {
        "@type": "Organization",
        "name": "Bamme Media",
        "telephone": "+4917645725863",
        "email": "bamme.media@gmail.com",
        "address": {
          "@type": "PostalAddress",
          "addressLocality": "Wernigerode",
          "postalCode": "38855",
          "addressRegion": "Sachsen-Anhalt",
          "addressCountry": "DE"
        }
      }
    }
  });

  const [formData, setFormData] = useState({
    name: '',
    email: '',
    company: '',
    phone: '',
    service: '',
    message: '',
  });
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [submitStatus, setSubmitStatus] = useState<'idle' | 'success' | 'error'>('idle');
  const [isVisible, setIsVisible] = useState(false);
  const sectionRef = useRef<HTMLDivElement>(null);

  useEffect(() => {
    const timer = setTimeout(() => setIsVisible(true), 100);
    return () => clearTimeout(timer);
  }, []);

  const handleChange = (e: React.ChangeEvent<HTMLInputElement | HTMLTextAreaElement | HTMLSelectElement>) => {
    const { name, value } = e.target;
    setFormData(prev => ({ ...prev, [name]: value }));
  };

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    if (!formData.name.trim() || !formData.email.trim() || !formData.message.trim()) return;
    if (formData.message.length > 500) return;

    setIsSubmitting(true);
    try {
      const body = new URLSearchParams();
      Object.entries(formData).forEach(([key, value]) => body.append(key, value));

      const res = await fetch('https://readdy.ai/api/form/d6s63qsddmmni7ck9830', {
        method: 'POST',
        headers: { 'Content-Type': 'application/x-www-form-urlencoded' },
        body: body.toString(),
      });

      if (res.ok) {
        setSubmitStatus('success');
        setFormData({ name: '', email: '', company: '', phone: '', service: '', message: '' });
      } else {
        setSubmitStatus('error');
      }
    } catch {
      setSubmitStatus('error');
    } finally {
      setIsSubmitting(false);
      setTimeout(() => setSubmitStatus('idle'), 8000);
    }
  };

  const services = [
    'Webdesign & Entwicklung',
    'Social Media Marketing',
    'Foto & Videographie',
    'Sonstiges',
  ];

  return (
    <div className="min-h-screen bg-black">

      {/* ── HERO ── */}
      <section className="relative bg-black overflow-hidden" style={{ minHeight: '100vh' }}>
        {/* Background image */}
        <div className="absolute inset-0">
          <img
            src="https://public.readdy.ai/ai/img_res/9b9dbbc1b0f7ebe96adaa96cf33443c5.jpg"
            alt="Bamme-Media Kontakt"
            className="w-full h-full object-cover object-center"
          />
        </div>
        {/* Overlay */}
        <div className="absolute inset-0 bg-gradient-to-b from-black/60 via-black/50 to-black"></div>

        {/* Hero Content */}
        <div
          className="relative z-10 flex flex-col items-center justify-center text-center px-6 pt-48 pb-32"
          style={{ minHeight: '100vh' }}
        >
          <p
            className={`text-white/50 text-xs uppercase tracking-[0.4em] mb-8 font-light transition-all duration-1000 ${isVisible ? 'opacity-100 translate-y-0' : 'opacity-0 translate-y-8'}`}
          >
            Bamme-Media · Wernigerode
          </p>
          <h1
            className={`text-7xl md:text-8xl lg:text-9xl font-bold text-white leading-[0.9] tracking-tight mb-8 transition-all duration-1000 delay-100 ${isVisible ? 'opacity-100 translate-y-0' : 'opacity-0 translate-y-8'}`}
          >
            KONTAKT &amp;
            <br />
            <span className="text-[#d1bc8a]">BERATUNG.</span>
          </h1>
          <p
            className={`mt-2 mb-4 text-base md:text-lg text-white/40 max-w-xl mx-auto font-light tracking-wide uppercase transition-all duration-1000 delay-150 ${isVisible ? 'opacity-100 translate-y-0' : 'opacity-0 translate-y-8'}`}
          >
            Webdesign · Social Media · Foto &amp; Video – Wernigerode
          </p>
          <p
            className={`mt-6 text-lg md:text-xl text-white/60 max-w-2xl mx-auto leading-relaxed font-light transition-all duration-1000 delay-200 ${isVisible ? 'opacity-100 translate-y-0' : 'opacity-0 translate-y-8'}`}
          >
            Du hast ein Projekt im Kopf? Wir hören zu –<br className="hidden md:block" />
            und machen daraus etwas Außergewöhnliches.
          </p>
          <div
            className={`mt-12 transition-all duration-1000 delay-300 ${isVisible ? 'opacity-100 translate-y-0' : 'opacity-0 translate-y-8'}`}
          >
            <a
              href="#kontakt-form"
              className="inline-flex items-center gap-3 px-10 py-5 text-sm font-medium text-black bg-white hover:bg-[#d1bc8a] transition-all duration-500 group whitespace-nowrap cursor-pointer uppercase tracking-widest"
            >
              Jetzt anfragen
              <i className="ri-arrow-right-line text-lg group-hover:translate-x-2 transition-transform duration-300"></i>
            </a>
          </div>

          {/* Scroll hint */}
          <div
            className={`absolute bottom-12 left-1/2 -translate-x-1/2 flex flex-col items-center gap-3 pointer-events-none transition-all duration-1000 delay-500 ${isVisible ? 'opacity-100' : 'opacity-0'}`}
          >
            <span className="text-white/40 text-xs uppercase tracking-[0.3em] font-light">Scroll</span>
            <div className="w-px h-16 bg-gradient-to-b from-white/40 to-transparent animate-pulse" />
          </div>
        </div>
      </section>

      {/* ── MARQUEE BANNER ── */}
      <div className="relative bg-[#f5f3ef] overflow-hidden">
        <div className="w-full h-px bg-zinc-200" />
        <div className="max-w-screen-xl mx-auto px-8 py-10">
          <div className="overflow-hidden relative">
            <div className="absolute left-0 top-0 bottom-0 w-16 bg-gradient-to-r from-[#f5f3ef] to-transparent z-10 pointer-events-none" />
            <div className="absolute right-0 top-0 bottom-0 w-16 bg-gradient-to-l from-[#f5f3ef] to-transparent z-10 pointer-events-none" />
            <div className="flex animate-marquee whitespace-nowrap">
              {[1, 2].map((i) => (
                <div key={i} className="flex items-center gap-12 px-6">
                  <span className="text-zinc-900 text-sm uppercase tracking-[0.25em] font-semibold">Webdesign</span>
                  <span className="text-[#d1bc8a] text-lg">—</span>
                  <span className="text-zinc-900 text-sm uppercase tracking-[0.25em] font-semibold">Branding</span>
                  <span className="text-[#d1bc8a] text-lg">—</span>
                  <span className="text-zinc-900 text-sm uppercase tracking-[0.25em] font-semibold">Social Media</span>
                  <span className="text-[#d1bc8a] text-lg">—</span>
                  <span className="text-zinc-900 text-sm uppercase tracking-[0.25em] font-semibold">Foto &amp; Video</span>
                  <span className="text-[#d1bc8a] text-lg">—</span>
                  <span className="text-zinc-900 text-sm uppercase tracking-[0.25em] font-semibold">Kostenlose Beratung</span>
                  <span className="text-[#d1bc8a] text-lg">—</span>
                </div>
              ))}
            </div>
          </div>
        </div>
        <div className="w-full h-px bg-zinc-200" />
      </div>

      {/* ── CONTACT INFO CARDS ── */}
      <section className="relative bg-black py-32 overflow-hidden">
        <div className="absolute top-0 left-10 w-96 h-96 bg-[#d1bc8a]/5 rounded-full blur-3xl pointer-events-none" />
        <div className="absolute bottom-0 right-10 w-96 h-96 bg-[#d1bc8a]/5 rounded-full blur-3xl pointer-events-none" />

        <div className="relative z-10 max-w-7xl mx-auto px-6">
          {/* Section Header */}
          <div className="text-center mb-20">
            <p className="text-white/40 text-xs uppercase tracking-[0.4em] mb-6 font-light">Kontakt</p>
            <h2 className="text-7xl md:text-8xl font-bold text-white tracking-tight leading-[0.9]">
              Bereit für Ihr
              <br />
              <span className="text-[#d1bc8a]">Projekt?</span>
            </h2>
            <p className="mt-8 text-xl text-white/60 max-w-2xl mx-auto leading-relaxed font-light">
              Lassen Sie uns gemeinsam etwas Großartiges schaffen
            </p>
            <a
              href="mailto:bamme.media@gmail.com"
              className="inline-block mt-10 text-2xl font-semibold text-[#d1bc8a] hover:text-white transition-all duration-500 group"
            >
              bamme.media@gmail.com
              <div className="h-0.5 bg-[#d1bc8a] transform origin-left scale-x-0 group-hover:scale-x-100 transition-transform duration-500" />
            </a>
          </div>

          {/* Cards */}
          <div className="grid md:grid-cols-3 gap-8 max-w-5xl mx-auto">
            {[
              { icon: 'ri-phone-line', title: 'Telefon', value: '017645725863', sub: 'Mo–Fr, 9–18 Uhr', href: 'tel:017645725863' },
              { icon: 'ri-mail-line', title: 'E-Mail', value: 'bamme.media@gmail.com', sub: 'Antwort innerhalb 24h', href: 'mailto:bamme.media@gmail.com' },
              { icon: 'ri-map-pin-line', title: 'Standort', value: '38855 Wernigerode', sub: 'Sachsen-Anhalt, Deutschland', href: null },
            ].map((card, i) => {
              const inner = (
                <>
                  <div className="w-20 h-20 mx-auto mb-6 border-2 border-white/20 rounded-full flex items-center justify-center group-hover:border-[#d1bc8a] group-hover:shadow-[0_0_30px_rgba(209,188,138,0.3)] transition-all duration-500">
                    <i className={`${card.icon} text-3xl text-white`}></i>
                  </div>
                  <h4 className="text-xl font-semibold text-white mb-2">{card.title}</h4>
                  <p className="text-gray-400 group-hover:text-[#d1bc8a] transition-colors duration-300 font-medium">{card.value}</p>
                  <p className="text-gray-600 text-sm mt-1">{card.sub}</p>
                </>
              );
              return card.href ? (
                <a
                  key={card.title}
                  href={card.href}
                  className="group text-center p-10 border border-white/10 rounded-2xl hover:border-[#d1bc8a]/40 hover:shadow-[0_0_40px_rgba(209,188,138,0.08)] transition-all duration-500 cursor-pointer"
                  style={{ transitionDelay: `${i * 100}ms` }}
                >
                  {inner}
                </a>
              ) : (
                <div
                  key={card.title}
                  className="group text-center p-10 border border-white/10 rounded-2xl transition-all duration-500"
                >
                  {inner}
                </div>
              );
            })}
          </div>
        </div>
      </section>

      {/* ── FORM SECTION ── */}
      <section id="kontakt-form" ref={sectionRef} className="relative bg-black pb-32 overflow-hidden">
        <div className="absolute inset-0 bg-gradient-to-b from-zinc-900/30 via-black to-black pointer-events-none" />

        <div className="relative z-10 max-w-7xl mx-auto px-6">
          <div className="grid lg:grid-cols-5 gap-16 items-start">

            {/* Left: Info */}
            <div className="lg:col-span-2">
              <h2 className="text-5xl font-bold text-white mb-6 leading-tight tracking-tight">
                Kostenlose<br />
                <span className="text-[#d1bc8a]">Erstberatung</span>
              </h2>
              <p className="text-white/50 leading-relaxed mb-12 text-lg font-light">
                Wir nehmen uns Zeit für dein Projekt. In einem unverbindlichen Gespräch analysieren wir deine Ziele und zeigen dir, wie wir gemeinsam den nächsten Schritt gehen können.
              </p>

              {/* Steps */}
              <div className="space-y-8">
                {[
                  { num: '01', title: 'Nachricht senden', desc: 'Füll das Formular aus – wir melden uns innerhalb von 24 Stunden.' },
                  { num: '02', title: 'Kostenloses Gespräch', desc: 'Wir besprechen dein Projekt und deine Ziele in einem kurzen Call.' },
                  { num: '03', title: 'Angebot erhalten', desc: 'Du bekommst ein maßgeschneidertes Angebot – transparent und fair.' },
                  { num: '04', title: 'Projekt starten', desc: 'Wir legen los und bringen deine Vision zum Leben.' },
                ].map((step) => (
                  <div key={step.num} className="flex gap-5">
                    <div className="flex-shrink-0 w-10 h-10 flex items-center justify-center">
                      <span className="text-[#d1bc8a] font-bold text-sm">{step.num}</span>
                    </div>
                    <div>
                      <h4 className="text-white font-semibold mb-1">{step.title}</h4>
                      <p className="text-white/40 text-sm leading-relaxed">{step.desc}</p>
                    </div>
                  </div>
                ))}
              </div>

              {/* Social */}
              <div className="mt-14 pt-10 border-t border-white/10">
                <p className="text-white/30 text-xs mb-5 uppercase tracking-widest">Folge uns</p>
                <div className="flex gap-3">
                  {[
                    { icon: 'ri-instagram-line', href: 'https://www.instagram.com/bamme.media/' },
                    { icon: 'ri-facebook-fill', href: 'https://www.facebook.com/bamme.media/' },
                    { icon: 'ri-linkedin-fill', href: 'https://linkedin.com' },
                    { icon: 'ri-youtube-fill', href: 'https://youtube.com' },
                  ].map((s) => (
                    <a
                      key={s.icon}
                      href={s.href}
                      target="_blank"
                      rel="noopener noreferrer"
                      className="w-11 h-11 flex items-center justify-center border border-white/20 rounded-xl text-white hover:border-[#d1bc8a] hover:text-[#d1bc8a] hover:shadow-[0_0_15px_rgba(209,188,138,0.2)] transition-all duration-300 cursor-pointer"
                    >
                      <i className={`${s.icon} text-lg`}></i>
                    </a>
                  ))}
                </div>
              </div>
            </div>

            {/* Right: Form */}
            <div className="lg:col-span-3">
              {/* Status Messages */}
              {submitStatus === 'success' && (
                <div className="mb-6 p-6 bg-green-500/10 border border-green-500/30 rounded-2xl text-green-400 flex items-center gap-3">
                  <i className="ri-checkbox-circle-line text-xl"></i>
                  <span>Vielen Dank! Deine Nachricht wurde erfolgreich gesendet. Wir melden uns bald.</span>
                </div>
              )}
              {submitStatus === 'error' && (
                <div className="mb-6 p-6 bg-red-500/10 border border-red-500/30 rounded-2xl text-red-400 flex items-center gap-3">
                  <i className="ri-error-warning-line text-xl"></i>
                  <span>Fehler beim Senden. Bitte versuche es erneut oder schreib uns direkt per E-Mail.</span>
                </div>
              )}

              <form onSubmit={handleSubmit} data-readdy-form id="kontakt-page-form" className="space-y-6">
                <div className="grid sm:grid-cols-2 gap-6">
                  <div>
                    <label className="block text-xs text-white/30 uppercase tracking-widest mb-3">Name *</label>
                    <input
                      type="text"
                      name="name"
                      value={formData.name}
                      onChange={handleChange}
                      required
                      placeholder="Max Mustermann"
                      className="w-full px-8 py-5 bg-transparent border-2 border-white/20 rounded-2xl text-white text-sm placeholder-white/20 focus:outline-none focus:border-[#d1bc8a] focus:shadow-[0_0_30px_rgba(209,188,138,0.2)] transition-all duration-500"
                    />
                  </div>
                  <div>
                    <label className="block text-xs text-white/30 uppercase tracking-widest mb-3">E-Mail *</label>
                    <input
                      type="email"
                      name="email"
                      value={formData.email}
                      onChange={handleChange}
                      required
                      placeholder="max@beispiel.de"
                      className="w-full px-8 py-5 bg-transparent border-2 border-white/20 rounded-2xl text-white text-sm placeholder-white/20 focus:outline-none focus:border-[#d1bc8a] focus:shadow-[0_0_30px_rgba(209,188,138,0.2)] transition-all duration-500"
                    />
                  </div>
                </div>

                <div className="grid sm:grid-cols-2 gap-6">
                  <div>
                    <label className="block text-xs text-white/30 uppercase tracking-widest mb-3">Unternehmen</label>
                    <input
                      type="text"
                      name="company"
                      value={formData.company}
                      onChange={handleChange}
                      placeholder="Dein Unternehmen"
                      className="w-full px-8 py-5 bg-transparent border-2 border-white/20 rounded-2xl text-white text-sm placeholder-white/20 focus:outline-none focus:border-[#d1bc8a] focus:shadow-[0_0_30px_rgba(209,188,138,0.2)] transition-all duration-500"
                    />
                  </div>
                  <div>
                    <label className="block text-xs text-white/30 uppercase tracking-widest mb-3">Telefon</label>
                    <input
                      type="tel"
                      name="phone"
                      value={formData.phone}
                      onChange={handleChange}
                      placeholder="+49 176 ..."
                      className="w-full px-8 py-5 bg-transparent border-2 border-white/20 rounded-2xl text-white text-sm placeholder-white/20 focus:outline-none focus:border-[#d1bc8a] focus:shadow-[0_0_30px_rgba(209,188,138,0.2)] transition-all duration-500"
                    />
                  </div>
                </div>

                <div>
                  <label className="block text-xs text-white/30 uppercase tracking-widest mb-3">Gewünschter Service</label>
                  <select
                    name="service"
                    value={formData.service}
                    onChange={handleChange}
                    className="w-full px-8 py-5 bg-black border-2 border-white/20 rounded-2xl text-sm focus:outline-none focus:border-[#d1bc8a] focus:shadow-[0_0_30px_rgba(209,188,138,0.2)] transition-all duration-500 cursor-pointer appearance-none text-white/40"
                  >
                    <option value="" className="bg-black text-white/40">Bitte wählen...</option>
                    {services.map(s => (
                      <option key={s} value={s} className="bg-black text-white">{s}</option>
                    ))}
                  </select>
                </div>

                <div>
                  <label className="block text-xs text-white/30 uppercase tracking-widest mb-3">Nachricht *</label>
                  <textarea
                    name="message"
                    value={formData.message}
                    onChange={handleChange}
                    required
                    rows={7}
                    maxLength={500}
                    placeholder="Erzähl uns von deinem Projekt, deinen Zielen und deinem Budget..."
                    className="w-full px-8 py-5 bg-transparent border-2 border-white/20 rounded-2xl text-white text-sm placeholder-white/20 focus:outline-none focus:border-[#d1bc8a] focus:shadow-[0_0_30px_rgba(209,188,138,0.2)] transition-all duration-500 resize-none"
                  />
                  <div className="flex justify-between mt-2">
                    <span className="text-xs text-white/20">Max. 500 Zeichen</span>
                    <span className={`text-xs ${formData.message.length > 450 ? 'text-[#d1bc8a]' : 'text-white/20'}`}>
                      {formData.message.length}/500
                    </span>
                  </div>
                </div>

                <div className="text-center pt-4">
                  <button
                    type="submit"
                    disabled={isSubmitting}
                    className="group relative inline-flex items-center justify-center px-16 py-6 text-lg font-semibold text-white border-2 border-white/30 rounded-full overflow-hidden transition-all duration-500 hover:border-[#d1bc8a] hover:shadow-[0_0_40px_rgba(209,188,138,0.4)] disabled:opacity-50 disabled:cursor-not-allowed whitespace-nowrap cursor-pointer"
                  >
                    <span className="absolute inset-0 bg-[#d1bc8a] transform scale-x-0 group-hover:scale-x-100 transition-transform duration-500 origin-left"></span>
                    <span className="relative z-10 flex items-center gap-3">
                      {isSubmitting ? (
                        <>
                          <div className="animate-spin rounded-full h-5 w-5 border-b-2 border-white"></div>
                          <span>Wird gesendet...</span>
                        </>
                      ) : (
                        <>
                          <span>Nachricht senden</span>
                          <i className="ri-arrow-right-line text-xl group-hover:translate-x-2 transition-transform duration-300"></i>
                        </>
                      )}
                    </span>
                  </button>
                </div>

                <p className="text-center text-xs text-white/20 leading-relaxed">
                  Mit dem Absenden stimmst du unserer{' '}
                  <a href="/datenschutz" className="text-white/40 hover:text-[#d1bc8a] transition-colors duration-200 underline">Datenschutzerklärung</a>{' '}
                  zu.
                </p>
              </form>
            </div>
          </div>
        </div>
      </section>

      {/* ── MAP ── */}
      <section className="max-w-7xl mx-auto px-6 pb-32">
        <div className="rounded-3xl overflow-hidden border border-white/10 h-80">
          <iframe
            title="Bamme Media Standort Wernigerode"
            src="https://www.google.com/maps/embed?pb=!1m18!1m12!1m3!1d2453.0!2d10.7865!3d51.8312!2m3!1f0!2f0!3f0!3m2!1i1024!2i768!4f13.1!3m3!1m2!1s0x47a5a3b3b3b3b3b3%3A0x0!2sWernigerode!5e0!3m2!1sde!2sde!4v1700000000000!5m2!1sde!2sde"
            width="100%"
            height="100%"
            style={{ border: 0, filter: 'invert(90%) hue-rotate(180deg)' }}
            allowFullScreen
            loading="lazy"
            referrerPolicy="no-referrer-when-downgrade"
          ></iframe>
        </div>
      </section>

    </div>
  );
};

export default KontaktPage;
